import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { createTheme, Theme, ThemeOptions } from "@mui/material/styles";
import { CSSProperties } from "react";
import { BREAKPOINT_VALUES, offsetBreakpointValues } from "../const";
import { darkColors, lightColors, redGreenColorblindColors } from "./colors";

type DataAttributeKey = `data-${string}`;
interface Styles {
    defaultBorder: string;
    altBorder1: string;
    altBorder2: string;
    altBorder3: string;
    altBorder4: string;
    flexCentered: CSSProperties;
    boxShadows: { boxShadow: string }[];
}
declare module "@mui/material/styles" {
    interface Palette {
        primaryBlack: Palette["primary"];
        primaryGreen: Palette["primary"];
        primaryPurple: Palette["primary"];
        primaryPurple2: Palette["primary"];
        primaryPurple3: Palette["primary"];
        primaryWhite: Palette["primary"];
        primaryBlue: Palette["primary"];
        secondaryBlue: Palette["primary"];
        secondaryGreen: Palette["primary"];
        secondaryPurple: Palette["primary"];
        secondaryRed: Palette["primary"];
        extendedGreen1: Palette["primary"];
        extendedGreen2: Palette["primary"];
        extendedGreen3: Palette["primary"];
        extendedGreen4: Palette["primary"];
        extendedGreen5: Palette["primary"];
        extendedYellow: Palette["primary"];
        extendedGrey1: Palette["primary"];
        extendedGrey2: Palette["primary"];
        extendedGrey3: Palette["primary"];
        extendedGrey4: Palette["primary"];
        extendedPurple: Palette["primary"];
        extendedPurple2: Palette["primary"];
        highlight: Palette["primary"];
        extendedPink1: Palette["primary"];
        tagGreen: Palette["primary"];
        tagBackgroundGreen: Palette["primary"];
        tagRed: Palette["primary"];
        tagBackgroundRed: Palette["primary"];
        tagMagenta: Palette["primary"];
        tagBackgroundMagenta: Palette["primary"];
        tagBrown: Palette["primary"];
        tagBackgroundBrown: Palette["primary"];
        tagGray: Palette["primary"];
        tagBackgroundGray: Palette["primary"];
        tagPurple: Palette["primary"];
        tagBackgroundPurple: Palette["primary"];
        navBackground: Palette["primary"];
        iconPrimary: Palette["primary"];
        supportWarning: Palette["primary"];
        borderDisabled: Palette["primary"];
        borderSubtle: Palette["primary"];
        tagBlue: Palette["primary"];
        tagBackgroundBlue: Palette["primary"];
        tagTeal: Palette["primary"];
        tagBackgroundTeal: Palette["primary"];
        marketOverviewBlue: Palette["primary"];
        notificationErrorBackground: Palette["primary"];
    }

    interface PaletteOptions {
        primaryBlack: PaletteOptions["primary"];
        primaryGreen: PaletteOptions["primary"];
        primaryPurple: PaletteOptions["primary"];
        primaryPurple2: PaletteOptions["primary"];
        primaryPurple3: PaletteOptions["primary"];
        primaryWhite: PaletteOptions["primary"];
        primaryBlue: PaletteOptions["primary"];
        secondaryBlue: PaletteOptions["primary"];
        secondaryGreen: PaletteOptions["primary"];
        secondaryPurple: PaletteOptions["primary"];
        secondaryRed: PaletteOptions["primary"];
        extendedGreen1: PaletteOptions["primary"];
        extendedGreen2: PaletteOptions["primary"];
        extendedGreen3: PaletteOptions["primary"];
        extendedGreen4: PaletteOptions["primary"];
        extendedGreen5: PaletteOptions["primary"];
        extendedYellow: PaletteOptions["primary"];
        extendedGrey1: PaletteOptions["primary"];
        extendedGrey2: PaletteOptions["primary"];
        extendedGrey3: PaletteOptions["primary"];
        extendedGrey4: PaletteOptions["primary"];
        extendedPurple: PaletteOptions["primary"];
        extendedPurple2: PaletteOptions["primary"];
        highlight: PaletteOptions["primary"];
        extendedPink1: PaletteOptions["primary"];
        tagGreen: PaletteOptions["primary"];
        tagBackgroundGreen: PaletteOptions["primary"];
        tagRed: PaletteOptions["primary"];
        tagMagenta: PaletteOptions["primary"];
        tagBackgroundMagenta: PaletteOptions["primary"];
        tagBrown: PaletteOptions["primary"];
        tagBackgroundBrown: PaletteOptions["primary"];
        tagGray: PaletteOptions["primary"];
        tagBackgroundGray: PaletteOptions["primary"];
        tagPurple: PaletteOptions["primary"];
        tagBackgroundPurple: PaletteOptions["primary"];
        navBackground: PaletteOptions["primary"];
        iconPrimary: PaletteOptions["primary"];
        supportWarning: PaletteOptions["primary"];
        borderDisabled: PaletteOptions["primary"];
        borderSubtle: PaletteOptions["primary"];
        tagBlue: PaletteOptions["primary"];
        tagBackgroundBlue: PaletteOptions["primary"];
        tagTeal: PaletteOptions["primary"];
        tagBackgroundTeal: PaletteOptions["primary"];
        marketOverviewBlue: PaletteOptions["primary"];
        notificationErrorBackground: PaletteOptions["primary"];
    }

    // Theme interface: stores the resolved styles
    interface Theme {
        styles: Styles;
    }

    // ThemeOptions: allows defining styles as a function
    interface ThemeOptions {
        styles: (palette: Palette) => Styles;
    }

    interface PaletteColor {
        darker?: string;
    }
    interface SimplePaletteColorOptions {
        darker?: string;
    }

    interface TypographyVariants {
        body1Bold: React.CSSProperties;
        body2Bold: React.CSSProperties;
    }

    interface TypographyVariants {
        body1Bold: React.CSSProperties;
        overline2: React.CSSProperties;
        caption2: React.CSSProperties;
        caption2Bold: React.CSSProperties;
    }

    // allow configuration using `createTheme`
    interface TypographyVariantsOptions {
        body1Bold?: React.CSSProperties;
        body2Bold?: React.CSSProperties;
        overline2?: React.CSSProperties;
        caption2?: React.CSSProperties;
        caption2Bold?: React.CSSProperties;
    }
    interface AccordionVariants {
        card?: React.CSSProperties;
        admin?: React.CSSProperties;
    }
    interface AccordionVariantsOptions {
        card?: React.CSSProperties;
        admin?: React.CSSProperties;
    }

    interface ChipVariants {
        success?: React.CSSProperties;
        danger?: React.CSSProperties;
        altSuccess?: React.CSSProperties;
        altInfo?: React.CSSProperties;
        default?: React.CSSProperties;
        green?: React.CSSProperties;
        black?: React.CSSProperties;
    }
}

interface ColorOverrides {
    primaryBlack: true;
    primaryGreen: true;
    primaryPurple: true;
    primaryPurple2: true;
    primaryPurple3: true;
    primaryWhite: true;
    primaryBlue: true;
    secondaryBlue: true;
    secondaryGreen: true;
    secondaryPurple: true;
    secondaryRed: true;
    extendedGreen1: true;
    extendedGreen2: true;
    extendedGreen3: true;
    extendedGreen4: true;
    extendedGreen5: true;
    extendedYellow: true;
    extendedGrey1: true;
    extendedGrey2: true;
    extendedGrey3: true;
    extendedGrey4: true;
    extendedPurple: true;
    extendedPurple2: true;
    extendedPink1: true;
    tagGreen: true;
    tagBackgroundGreen: true;
    tagRed: true;
    tagBackgroundRed: true;
    tagGray: true;
    tagBackgroundGray: true;
    tagPurple: true;
    tagBackgroundPurple: true;
    tagMagenta: true;
    tagBackgroundMagenta: true;
    tagBrown: true;
    tagBackgroundBrown: true;
    navBackground: true;
    iconPrimary: true;
    supportWarning: true;
    borderDisabled: true;
    borderSubtle: true;
    tagBlue: true;
    tagBackgroundBlue: true;
    tagTeal: true;
    tagBackgroundTeal: true;
    marketOverviewBlue: true;
    notificationErrorBackground: true;
}

declare module "@mui/material/Typography" {
    interface TypographyPropsColorOverrides extends ColorOverrides {}
    interface TypographyPropsVariantOverrides {
        body1Bold: true;
        body2Bold: true;
        overline2: true;
        caption2: true;
        caption2Bold: true;
    }
}
declare module "@mui/material/Button" {
    interface ButtonPropsColorOverrides extends ColorOverrides {}
    interface ButtonPropsVariantOverrides {
        hollow: true;
    }
}

declare module "@mui/material/ButtonGroup" {
    interface ButtonGroupPropsColorOverrides extends ColorOverrides {}
    interface ButtonGroupPropsVariantOverrides {
        hollow: true;
    }
}

declare module "@mui/material/ToggleButton" {
    interface ToggleButtonPropsColorOverrides extends ColorOverrides {}
}

declare module "@mui/material/Slider" {
    interface SliderPropsColorOverrides extends ColorOverrides {}
}
declare module "@mui/material/SvgIcon" {
    interface SvgIconPropsColorOverrides extends ColorOverrides {}
}
declare module "@mui/material/Fab" {
    interface FabPropsColorOverrides extends ColorOverrides {}
}
declare module "@mui/material/Paper" {
    interface PaperPropsVariantOverrides {
        card: true;
        admin: true;
    }
}
declare module "@mui/material/Chip" {
    interface ChipPropsVariantOverrides {
        success: true;
        info: true;
        danger: true;
        altSuccess: true;
        altInfo: true;
        default: true;
        magenta: true;
        brown: true;
        black: true;
        green: true;
        blue: true;
        teal: true;
    }
}

declare module "@mui/material/Radio" {
    interface RadioPropsColorOverrides extends ColorOverrides {}
    interface InputProps extends React.InputHTMLAttributes<any> {
        [dataAttribute: DataAttributeKey]: any;
    }
    interface RadioProps {
        inputProps?: {
            "data-testid"?: string;
        } & React.InputHTMLAttributes<HTMLInputElement>;
    }
}
declare module "@mui/material/Checkbox" {
    interface CheckboxPropsColorOverrides extends ColorOverrides {}
    interface InputProps extends React.InputHTMLAttributes<any> {
        [dataAttribute: DataAttributeKey]: any;
    }
}

export const themeOptions: ThemeOptions = {
    styles: (palette) => ({
        defaultBorder: `1px solid ${palette.extendedGreen3.main}`,
        altBorder1: `1px solid ${palette.extendedGreen2.main}`,
        altBorder2: `1px solid ${palette.extendedGreen1.main}`,
        altBorder3: `1px solid ${palette.borderDisabled.main}`,
        altBorder4: `1px solid ${palette.borderSubtle.main}`,
        flexCentered: { display: "flex", justifyContent: "center" },
        boxShadows: [{ boxShadow: "0px 0px 25px rgba(0, 0, 0, 0.15)" }],
    }),
    typography: (palette) => ({
        h1: {
            fontFamily: "Financier",
            fontSize: "74px",
            lineHeight: "74px",
            letterSpacing: "1px",
            textTransform: "none",
            color: palette.primaryBlack.main,
        },
        h2: {
            fontFamily: "Financier",
            fontSize: "48px",
            lineHeight: "48px",
            letterSpacing: "1px",
            textTransform: "none",
            color: palette.primaryBlack.main,
        },
        h3: {
            fontFamily: "Inter-SemiBold, proxima-soft, Arial, sans-serif",
            fontSize: "31px",
            lineHeight: "48px",
            letterSpacing: "0.5px",
            textTransform: "none",
            color: palette.primaryBlack.main,
        },
        h4: {
            fontFamily: "Inter-SemiBold, proxima-soft, Arial, sans-serif",
            fontSize: "25px",
            lineHeight: "40px",
            letterSpacing: "0.4px",
            textTransform: "none",
            color: palette.primaryBlack.main,
        },
        h5: {
            fontFamily: "Inter-SemiBold, proxima-soft, Arial, sans-serif",
            fontSize: "18px",
            lineHeight: "28px",
            letterSpacing: "0.4px",
            textTransform: "none",
            color: palette.primaryBlack.main,
        },
        body1: {
            fontFamily: "Inter-Medium, proxima-soft, Arial, sans-serif",
            fontSize: "16px",
            lineHeight: "24px",
            letterSpacing: "0.4px",
            textTransform: "none",
            color: palette.primaryBlack.main,
        },
        body1Bold: {
            fontFamily: "Inter-Bold, proxima-soft, Arial, sans-serif",
            fontSize: "16px",
            lineHeight: "24px",
            letterSpacing: "0.4px",
            textTransform: "none",
            color: palette.primaryBlack.main,
        },
        body2: {
            fontFamily: "Inter-Medium, proxima-soft, Arial, sans-serif",
            fontSize: "14px",
            lineHeight: "24px",
            letterSpacing: "0.2px",
            textTransform: "none",
            color: palette.primaryBlack.main,
        },
        body2Bold: {
            fontFamily: "Inter-Bold, proxima-soft, Arial, sans-serif",
            fontSize: "14px",
            lineHeight: "24px",
            letterSpacing: "0.4px",
            textTransform: "none",
            color: palette.primaryBlack.main,
        },
        button: {
            fontFamily: "Inter-SemiBold, proxima-soft, Arial, sans-serif",
            fontSize: "16px",
            lineHeight: "20px",
            letterSpacing: "0.4px",
            textTransform: "none",
            color: palette.primaryBlack.main,
        },
        caption: {
            fontFamily: "Inter-SemiBold, proxima-soft, Arial, sans-serif",
            fontSize: "12px",
            lineHeight: "16px",
            letterSpacing: "0.4px",
            textTransform: "uppercase",
            color: palette.primaryBlack.main,
        },
        overline: {
            fontFamily: "Inter-ExtraBold, proxima-soft, Arial, sans-serif",
            fontSize: "11px",
            lineHeight: "16px",
            letterSpacing: "1.2px",
            textTransform: "uppercase",
            color: palette.primaryBlack.main,
        },
        overline2: {
            fontFamily: "Inter-ExtraBold, proxima-soft, Arial, sans-serif",
            fontSize: "11px",
            lineHeight: "16px",
            letterSpacing: "1.2px",
            textTransform: "uppercase",
            color: palette.extendedGrey1.main,
        },
        caption2: {
            display: "block",
            fontFamily: "Inter-Medium, proxima-soft, Arial, sans-serif",
            fontSize: "8px",
            lineHeight: "12px",
            letterSpacing: "0.2px",
            color: palette.extendedGrey1.main,
        },
        caption2Bold: {
            display: "block",
            fontFamily: "Inter-Bold, proxima-soft, Arial, sans-serif",
            fontSize: "8px",
            lineHeight: "12px",
            letterSpacing: "0.2px",
            color: palette.extendedGrey1.main,
        },
    }),
    components: {
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    body1Bold: "p",
                    body2Bold: "p",
                    overline2: "div",
                    overline: "div",
                },
            },

            styleOverrides: {
                h1: ({ theme }) => ({
                    [theme.breakpoints.only("xs")]: {
                        fontSize: "31px",
                    },
                }),
                h2: ({ theme }) => ({
                    [theme.breakpoints.only("xs")]: {
                        fontSize: "25px",
                        lineHeight: "32px",
                    },
                }),
            },
        },
        // MuiCssBaseline: {
        //     styleOverrides: (theme) => ({
        //         // REVIEW this is for dark mode scrollbar
        //         // "*::-webkit-scrollbar": {
        //         //     width: "8px",
        //         //     height: "8px",
        //         // },
        //         // "*::-webkit-scrollbar-thumb": {
        //         //     backgroundColor:
        //         //         theme.palette.mode === "dark"
        //         //             ? "rgba(100, 100, 100, 0.6)"
        //         //             : "rgba(160, 160, 160, 0.6)",
        //         //     borderRadius: "4px",
        //         // },
        //         // "*::-webkit-scrollbar-thumb:hover": {
        //         //     backgroundColor:
        //         //         theme.palette.mode === "dark"
        //         //             ? "rgba(100, 100, 100, 0.8)"
        //         //             : "rgba(120, 120, 120, 0.8)",
        //         // },
        //         // "*": {
        //         //     scrollbarColor:
        //         //         theme.palette.mode === "dark"
        //         //             ? "rgba(100, 100, 100, 0.6) transparent"
        //         //             : "rgba(160, 160, 160, 0.6) transparent",
        //         // },
        //     }),
        // },
        // TODO check if I need !important tag and see if I can update the comment to show default and styles and maybe remove unused props
        // MuiContainer: {
        //     styleOverrides: {
        //         root: ({ theme }) => ({
        //             // boxSizing: "content-box",
        //             [theme.breakpoints.up("xs")]: {
        //                 // width: "calc(100% - 2rem)",
        //             },
        //             [theme.breakpoints.up("sm")]: {
        //                 // width: "calc(100% - 4rem)",
        //             },
        //         }),
        //         maxWidthSm: {
        //             maxWidth: "560px",
        //             "@media (min-width:600px)": {
        //                 maxWidth: "560px",
        //             },
        //         },
        //         maxWidthMd: {
        //             maxWidth: "944px",
        //             "@media (min-width:900px)": {
        //                 maxWidth: "944px",
        //             },
        //         },
        //         maxWidthLg: {
        //             maxWidth: "1136px",
        //             "@media (min-width:1200px)": {
        //                 maxWidth: "1136px",
        //             },
        //         },
        //         maxWidthXl: {
        //             maxWidth: "1376px",
        //             "@media (min-width:1440px)": {
        //                 maxWidth: "1376px",
        //             },
        //             "@media (min-width:1536px)": {
        //                 maxWidth: "1376px",
        //             },
        //         },
        //     },
        //     defaultProps: {
        //         maxWidth: "lg",
        //         // disableGutters: true,
        //     },
        // },
        MuiButton: {
            defaultProps: {
                variant: "contained",
                color: "primaryPurple",
                size: "large",
            },
            styleOverrides: {
                root: {
                    fontSize: "14px",
                    borderRadius: "2px",
                    boxShadow: "none",
                    overflow: "hidden",
                },
                sizeLarge: {
                    height: "48px",
                    padding: "14px 16px",
                },
                sizeSmall: {
                    minWidth: "10px",
                    padding: 0,
                    lineHeight: "24px",
                    "&:focus, &:hover": {
                        backgroundColor: "transparent",
                    },
                },
                contained: ({ ownerState, theme }) => {
                    const color =
                        ownerState.color && ownerState.color !== "inherit" && theme.palette[ownerState.color]
                            ? ownerState.color
                            : "primary";
                    const { main, dark, darker } = theme.palette[color];
                    return {
                        "&:focus": {
                            backgroundColor: darker || dark || main,
                        },
                        "&.Mui-disabled": {
                            backgroundColor: theme.palette.extendedGrey2.main,
                            color: theme.palette.extendedGrey1.main,
                        },
                    };
                },
                outlined: ({ ownerState, theme }) => {
                    const color =
                        ownerState.color && ownerState.color !== "inherit" && theme.palette[ownerState.color]
                            ? ownerState.color
                            : "primary";
                    const { contrastText, main, dark, darker } = theme.palette[color];
                    return {
                        "&:focus, &:hover": {
                            backgroundColor: contrastText,
                        },
                        "&:focus": {
                            borderColor: darker || dark || main,
                            color: darker || dark || main,
                        },
                        "&:hover": {
                            color: dark || main,
                        },
                        "&.Mui-disabled": {
                            borderColor: theme.palette.extendedGrey1.main,
                            color: theme.palette.extendedGrey1.main,
                        },
                    };
                },
                text: ({ ownerState, theme }) => {
                    const color =
                        ownerState.color && ownerState.color !== "inherit" && theme.palette[ownerState.color]
                            ? ownerState.color
                            : "primary";
                    const { contrastText, main, dark, darker } = theme.palette[color];
                    return {
                        fontFamily: "Inter-Medium, proxima-soft, Arial, sans-serif",
                        textDecoration: "underline",
                        textUnderlineOffset: "2px",
                        "&:focus, &:hover": {
                            backgroundColor: contrastText,
                        },
                        "&:focus": {
                            color: darker || dark || main,
                        },
                        "&:hover": {
                            color: dark || main,
                            textDecoration: "underline",
                        },
                        "&.Mui-disabled": {
                            color: theme.palette.extendedGrey1.main,
                        },
                    };
                },
            },
            variants: [
                {
                    props: { variant: "hollow" },
                    style: {
                        color: lightColors.primaryWhite,
                        background: "transparent",
                        border: `1px solid ${lightColors.primaryWhite}`,
                        borderRadius: "4px",
                    },
                },
            ],
        },
        MuiButtonGroup: {
            defaultProps: {
                color: "primaryPurple",
                size: "large",
            },
            styleOverrides: {
                root: {
                    fontSize: "14px",
                    borderRadius: "2px",
                    boxShadow: "none",
                    overflow: "hidden",
                },
                contained: ({ ownerState, theme }) => {
                    const color =
                        ownerState.color && ownerState.color !== "inherit" && theme.palette[ownerState.color]
                            ? ownerState.color
                            : "primary";
                    const { main, dark, darker } = theme.palette[color];
                    return {
                        "&:focus": {
                            backgroundColor: darker || dark || main,
                        },
                        "&.Mui-disabled": {
                            backgroundColor: theme.palette.extendedGrey2.main,
                            color: theme.palette.extendedGrey1.main,
                        },
                    };
                },
                outlined: ({ ownerState, theme }) => {
                    const color =
                        ownerState.color && ownerState.color !== "inherit" && theme.palette[ownerState.color]
                            ? ownerState.color
                            : "primary";
                    const { contrastText, main, dark, darker } = theme.palette[color];
                    return {
                        "&:focus, &:hover": {
                            backgroundColor: contrastText,
                        },
                        "&:focus": {
                            borderColor: darker || dark || main,
                            color: darker || dark || main,
                        },
                        "&:hover": {
                            color: dark || main,
                        },
                        "&.Mui-disabled": {
                            borderColor: theme.palette.extendedGrey1.main,
                            color: theme.palette.extendedGrey1.main,
                        },
                    };
                },
                text: ({ ownerState, theme }) => {
                    const color =
                        ownerState.color && ownerState.color !== "inherit" && theme.palette[ownerState.color]
                            ? ownerState.color
                            : "primary";
                    const { contrastText, main, dark, darker } = theme.palette[color];
                    return {
                        fontFamily: "Inter-Medium, proxima-soft, Arial, sans-serif",
                        textDecoration: "underline",
                        textUnderlineOffset: "2px",
                        "&:focus, &:hover": {
                            backgroundColor: contrastText,
                        },
                        "&:focus": {
                            color: darker || dark || main,
                        },
                        "&:hover": {
                            color: dark || main,
                            textDecoration: "underline",
                        },
                        "&.Mui-disabled": {
                            color: theme.palette.extendedGrey1.main,
                        },
                    };
                },
            },
            variants: [
                {
                    props: { variant: "hollow" },
                    style: {
                        color: lightColors.primaryWhite,
                        background: "transparent",
                        border: `1px solid ${lightColors.primaryWhite}`,
                        borderRadius: "4px",
                    },
                },
            ],
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    "& .MuiOutlinedInput-root": {
                        height: "48px",
                        padding: "0 16px !important",
                    },
                },
                input: {
                    height: "1.437em",
                    padding: "0 !important",
                    "&.MuiInputBase-inputAdornedStart": {
                        paddingLeft: "8px !important",
                    },
                    "&.MuiInputBase-inputAdornedEnd": {
                        paddingRight: "8px !important",
                    },
                },
                paper: {
                    boxShadow: "0px 0px 25px rgba(0, 0, 0, 0.15)",
                },
                listbox: ({ theme }) => ({
                    border: `1px solid ${theme.palette.extendedGreen2.main}`,
                    borderRadius: "2px",
                    padding: "0.25rem",
                }),
                option: ({ theme }) => ({
                    "&.Mui-focusVisible, &.Mui-focused": {
                        backgroundColor: `${theme.palette.extendedGrey2.main} !important`,
                    },
                }),
                popper: ({ ownerState }) => ({
                    "& .MuiAutocomplete-noOptions": {
                        display: ownerState.noOptionsText ? "block" : "none",
                    },
                }),
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    flex: "1",
                    padding: "1rem",
                    fontSize: "14px",
                    borderRadius: "2px",
                    color: theme.palette.primaryPurple.main,
                    borderColor: theme.palette.primaryPurple.main,
                    "&.Mui-selected, &.Mui-selected:hover": {
                        color: theme.palette.primaryWhite.main,
                        backgroundColor: theme.palette.primaryPurple.main,
                    },
                }),
            },
        },
        MuiCheckbox: {
            defaultProps: {
                color: "primaryBlack",
            },
            styleOverrides: {
                root: ({ ownerState, theme }) => {
                    const color =
                        ownerState.color && ownerState.color !== "default" && theme.palette[ownerState.color]
                            ? ownerState.color
                            : "primary";
                    const { main } = theme.palette[color];
                    return {
                        height: "24px",
                        width: "24px",
                        color: main,
                        padding: "3px",
                    };
                },
            },
        },
        MuiRadio: {
            defaultProps: {
                color: "primaryBlack",
            },
            styleOverrides: {
                root: ({ ownerState, theme }) => {
                    const color =
                        ownerState.color && ownerState.color !== "default" && theme.palette[ownerState.color]
                            ? ownerState.color
                            : "primary";
                    const { main } = theme.palette[color];
                    return {
                        height: "24px",
                        width: "24px",
                        color: main,
                        padding: "2px",
                    };
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: ({ theme, ownerState }) => ({
                    minHeight: "48px",
                    width: "100%",
                    borderRadius: "2px",
                    fontSize: "14px",
                    fontFamily: "Inter-Medium, proxima-soft, Arial, sans-serif",
                    lineHeight: ownerState.multiline ? "24px" : "48px",
                    letterSpacing: "0.2px",
                    backgroundColor: theme.palette.primaryWhite.main,
                    "& fieldset": {
                        borderColor: theme.palette.extendedGreen2.main,
                    },
                    "&:not(.Mui-error):not(.Mui-disabled)": {
                        "&:hover fieldset": {
                            borderColor: theme.palette.extendedGreen2.main,
                        },
                        "& input:focus + fieldset, &.Mui-focused fieldset": {
                            borderColor: theme.palette.primaryBlack.main,
                        },
                    },

                    "&:not(.Mui-error)": {
                        color: theme.palette.extendedGrey1.main,
                    },
                    "&.Mui-error": {
                        color: theme.palette.secondaryRed.main,
                        "& fieldset": {
                            borderWidth: "2px",
                        },
                    },
                    "&.Mui-disabled": {
                        backgroundColor: theme.palette.extendedGrey2.main,
                        "& fieldset": {
                            ...(theme.palette.mode !== "dark" && {
                                borderColor: `${theme.palette.extendedGreen3.main} !important`,
                            }),
                        },
                    },
                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                        WebKitAppearance: "none",
                        margin: 0,
                    },
                    "& input[type=number]": {
                        MozAppearance: "textfield",
                    },
                    "& .css-ihdtdm": {
                        width: 0,
                    },
                    "&.MuiInputBase-multiline": {
                        "& textarea": {
                            padding: 0,
                        },
                    },
                }),
                input: ({ theme }) => ({
                    padding: "0 16px",
                    "&.MuiInputBase-inputAdornedStart": {
                        paddingLeft: "8px",
                    },
                    "&.MuiInputBase-inputAdornedEnd": {
                        paddingRight: "8px",
                    },
                    color: theme.palette.primaryBlack.main,
                    WebkitTextFillColor: `${theme.palette.primaryBlack.main} !important`,
                    // NOTE by default MUI had a height constraint. No idea why but it was causing issues with unintended blur by the user.
                    height: "unset",
                }),
            },
        },
        MuiSelect: {
            styleOverrides: {
                icon: ({ theme }) => ({
                    color: theme.palette.primaryBlack.main,
                }),
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    marginLeft: 0,
                    width: "fit-content",
                    gap: "0.5rem",
                    ".MuiCheckbox-root + .MuiFormControlLabel-label, .MuiRadio-root + .MuiFormControlLabel-label":
                        {
                            fontSize: "14px",
                            lineHeight: "24px",
                            letterSpacing: "0.2px",
                        },
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: ({ theme }) => ({
                    fontFamily: "Inter-Bold, proxima-soft, Arial, sans-serif",
                    fontSize: "14px",
                    lineHeight: "24px",
                    letterSpacing: "0.2px",
                    "&:not(.Mui-error)": {
                        color: theme.palette.primaryBlack.main,
                    },
                    "&.Mui-error": {
                        color: theme.palette.secondaryRed.main,
                    },
                }),
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: ({ theme }) => ({
                    marginTop: 0,
                    fontFamily: "Inter-Medium, proxima-soft, Arial, sans-serif",
                    fontSize: "14px",
                    lineHeight: "24px",
                    letterSpacing: "0.2px",
                    textTransform: "none",
                    "&:not(.Mui-error):not(.Mui-disabled)": {
                        color: theme.palette.primaryBlack.main,
                    },
                    "&.Mui-disabled": {
                        color: theme.palette.extendedGrey1.main,
                    },
                    "&.Mui-error": {
                        color: theme.palette.secondaryRed.main,
                    },
                }),
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: ({ theme }) => ({
                    "& .MuiButtonBase-root": {
                        fontFamily: "Inter-Medium, proxima-soft, Arial, sans-serif",
                        fontSize: "14px",
                        lineHeight: "24px",
                        letterSpacing: "0.2px",
                        color: theme.palette.primaryBlack.main,
                    },
                    "& .Mui-selected": {
                        color: `${theme.palette.primaryPurple.main} !important`,
                        fontFamily: "Inter-Bold, proxima-soft, Arial, sans-serif",
                    },
                    "& .MuiTabs-indicator": {
                        backgroundColor: theme.palette.primaryPurple.main,
                    },
                    "& .Mui-disabled": {
                        color: theme.palette.extendedGrey1.main,
                    },
                }),
                vertical: ({ theme }) => ({
                    "& .MuiButtonBase-root": {
                        maxWidth: "100%",
                        height: "64px",
                        borderBottom: "2px solid transparent",
                    },
                    "& .Mui-selected": {
                        borderBottom: `2px solid ${theme.palette.primaryPurple.main}`,
                    },
                    "& .MuiTabs-indicator": {
                        display: "none",
                    },
                    "& button:not(:last-child)": {
                        marginBottom: "24px",
                    },
                    "& .MuiTabs-flexContainer": {
                        padding: "16px",
                        backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='5' ry='5' stroke='%239747FFFF' stroke-width='1' stroke-dasharray='10%2c 5' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
                    },
                }),
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: ({ theme }) => ({
                    padding: "16px",
                    backgroundColor: theme.palette.primaryBlack.main,
                    color: theme.palette.primaryWhite.main,
                    borderRadius: "2px",
                }),
            },
        },
        MuiSlider: {
            styleOverrides: {
                root: ({ theme }) => ({
                    height: 8,
                    paddingTop: 35,
                    "& .MuiSlider-valueLabel": {
                        fontSize: 12,
                        fontWeight: "normal",
                        top: -12,
                        backgroundColor: "unset",
                        color: theme.palette.primaryBlack.main,
                        "&:before": {
                            display: "none",
                        },
                        "& *": {
                            background: "transparent",
                        },
                    },
                    "& .MuiSlider-thumb": {
                        height: 24,
                        width: 24,
                        color: theme.palette.primaryPurple.main,
                    },
                    "& .MuiSlider-thumb:hover": {
                        boxShadow: "0px 0px 0px 8px rgba(221, 206, 247, 0.5)",
                    },
                    "& .MuiSlider-thumb.Mui-active": {
                        boxShadow: "0px 0px 0px 14px rgba(221, 206, 247, 0.5)",
                    },
                    "& .MuiSlider-track": {
                        backgroundColor: theme.palette.primaryPurple.main,
                    },
                    "& .MuiSlider-rail": {
                        backgroundColor: theme.palette.extendedPurple.main,
                    },
                }),
            },
        },
        MuiTable: {
            styleOverrides: {
                root: ({ theme }) => ({
                    "& tr": {
                        borderTop: `1px solid ${theme.palette.extendedGreen3.main}`,
                    },
                    "& thead tr": {
                        borderTop: "none",
                    },
                    "& th, td": {
                        border: "none",
                        padding: "12px 16px",
                    },
                }),
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.primaryBlack.main,
                }),
            },
        },
        MuiTableSortLabel: {
            styleOverrides: {
                root: ({ theme }) => ({
                    "&.MuiButtonBase-root": {
                        color: theme.palette.primaryBlack.main,
                    },
                }),
                icon: ({ theme }) => ({
                    color: `${theme.palette.primaryBlack.main} !important`,
                    fontSize: "24px",
                    margin: "0 0 0 8px",
                    opacity: 0.5,
                }),
            },
        },
        MuiAccordion: {
            variants: [
                {
                    props: { variant: "card" },
                    style: ({ theme }) => ({
                        padding: 0,
                        "&::before": { content: "none" },
                        backgroundColor: theme.palette.extendedGrey2.main,
                        backgroundImage: "unset",
                        border: theme.styles.altBorder3,
                        borderRadius: "8px",
                        marginBottom: 0,
                        "&.Mui-expanded": { marginTop: 0, marginBottom: 0 },
                        boxShadow: "none",
                        "&.Mui-disabled": {
                            backgroundColor: "unset",
                        },
                        "& .MuiAccordionSummary-root": {
                            padding: "1rem",
                            "&.Mui-expanded": {
                                minHeight: "0",
                                margin: 0,
                                // padding: "1rem 1rem 0 1rem",
                            },
                        },
                        "& .MuiAccordionDetails-root": {
                            padding: "0 1rem 1rem 1rem",
                        },
                    }),
                },
                {
                    props: { variant: "admin" },
                    style: ({ theme }) => ({
                        border: `1px solid ${theme.palette.extendedGreen2.main}`,
                        "& .MuiAccordionSummary-root": {
                            padding: "8px 16px",
                        },
                        "& .MuiAccordionDetails-root": {
                            padding: 0,
                        },
                    }),
                },
            ],
            styleOverrides: {
                root: {
                    padding: 0,
                    boxShadow: "none",
                    backgroundImage: "unset",
                    "&::before": { content: "none" },
                    "&.Mui-disabled": {
                        backgroundColor: "unset",
                    },
                },
            },
        },
        MuiAccordionSummary: {
            defaultProps: {
                expandIcon: <ExpandMoreIcon />,
            },
            styleOverrides: {
                root: {
                    gap: "1rem",
                    padding: "0.5rem",
                    minHeight: "0",
                    "&.Mui-expanded": {
                        minHeight: "0",
                        margin: 0,
                    },
                    "&.Mui-disabled": {
                        opacity: "1",
                        ".MuiAccordionSummary-expandIconWrapper": {
                            display: "none",
                        },
                    },
                },
                content: {
                    margin: `0`,
                    "&.Mui-expanded": {
                        margin: `0`,
                        minHeight: "0",
                    },
                },
                expandIconWrapper: ({ theme }) => ({
                    color: theme.palette.primaryBlack.main,
                }),
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    padding: "1rem 0 0 0 ",
                },
            },
        },
        MuiSnackbarContent: {
            styleOverrides: {
                root: ({ theme }) => ({
                    backgroundColor: theme.palette.primaryBlack.main,
                    padding: 0,
                    borderRadius: "2px",
                    width: "344px",
                    maxWidth: "344px",
                }),
                message: {
                    padding: "12px 16px",
                },
                action: {
                    marginRight: 0,
                    paddingLeft: 0,
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    "&:hover": {
                        background: theme.palette.extendedGrey2.main,
                    },
                }),
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderColor: theme.palette.extendedGreen3.main,
                }),
            },
        },
        MuiStack: {
            defaultProps: {
                useFlexGap: true,
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderColor: theme.palette.extendedGreen3.main,
                    width: 52,
                    height: 32,
                    padding: 0,
                    "& .MuiSwitch-thumb": {
                        boxSizing: "border-box",
                        width: 28,
                        height: 28,
                    },
                    "& .MuiSwitch-track": {
                        borderRadius: 32 / 2,
                        backgroundColor: theme.palette.extendedGrey1.main,
                        opacity: 1,
                        transition: theme.transitions.create(["background-color"], {
                            duration: 500,
                        }),
                    },
                    "& .MuiSwitch-switchBase": {
                        padding: 0,
                        margin: 2,
                        transitionDuration: "300ms",
                        "&.Mui-checked": {
                            transform: "translateX(20px)",
                            color: "#fff",
                            "& + .MuiSwitch-track": {
                                backgroundColor: theme.palette.primaryPurple.main,
                                opacity: 1,
                                border: 0,
                            },
                            "&.Mui-disabled + .MuiSwitch-track": {
                                opacity: 0.5,
                            },
                        },
                        "&.Mui-disabled + .MuiSwitch-track": {
                            opacity: 0.7,
                        },
                    },
                }),
            },
        },
        MuiLinearProgress: {
            styleOverrides: {
                root: ({ theme }) => ({
                    backgroundColor: theme.palette.primaryPurple.main,
                    borderRadius: "4px",
                    "& .MuiLinearProgress-bar": {
                        backgroundColor: theme.palette.extendedPurple.main,
                    },
                }),
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: ({ theme }) => ({
                    boxShadow: "none",
                    color: "black !important",
                    borderBottom: theme.styles.altBorder1,
                    backgroundColor: theme.palette.primaryWhite.main,
                }),
            },
        },
        MuiChip: {
            variants: [
                {
                    props: { variant: "danger" },
                    style: ({ theme }) => ({
                        background: theme.palette.tagBackgroundRed.main,
                        "& .MuiChip-label": {
                            color: theme.palette.tagRed.main,
                        },
                    }),
                },
                {
                    props: { variant: "success" },
                    style: ({ theme }) => ({
                        background: theme.palette.tagBackgroundGreen.main,
                        "& .MuiChip-label": {
                            color: theme.palette.tagGreen.main,
                        },
                    }),
                },
                {
                    props: { variant: "info" },
                    style: ({ theme }) => ({
                        background: theme.palette.secondaryBlue.main,
                        "& .MuiChip-label": {
                            color: theme.palette.primaryBlue.main,
                        },
                    }),
                },
                {
                    props: { variant: "altSuccess" },
                    style: ({ theme }) => ({
                        background: theme.palette.extendedGreen3.main,
                        "& .MuiChip-label": {
                            color: theme.palette.primaryGreen.main,
                        },
                    }),
                },
                {
                    props: { variant: "altInfo" },
                    style: () => ({
                        background: "#bae6ff",
                        "& .MuiChip-label": {
                            color: "#00539a",
                        },
                    }),
                },
                {
                    props: { variant: "default" },
                    style: ({ theme }) => ({
                        background: theme.palette.tagBackgroundGray.main,
                        "& .MuiChip-label": {
                            color: theme.palette.tagGray.main,
                        },
                    }),
                },
                {
                    props: { variant: "outlined" },
                    style: ({ theme }) => ({
                        border: `1px solid ${theme.palette.tagGray.main}`,
                    }),
                },
                {
                    props: { variant: "magenta" },
                    style: ({ theme }) => ({
                        background: theme.palette.tagBackgroundMagenta.main,
                        "& .MuiChip-label": {
                            textTransform: "unset",
                            letterSpacing: "unset",
                            color: theme.palette.tagMagenta.main,
                        },
                    }),
                },
                {
                    props: { variant: "brown" },
                    style: ({ theme }) => ({
                        background: theme.palette.tagBackgroundBrown.main,
                        "& .MuiChip-label": {
                            textTransform: "unset",
                            letterSpacing: "unset",
                            color: theme.palette.tagBrown.main,
                        },
                    }),
                },
                {
                    props: { variant: "black" },
                    style: ({ theme }) => ({
                        background: theme.palette.primaryBlack.main,
                        "& .MuiChip-label": {
                            color: theme.palette.primaryWhite.main,
                        },
                    }),
                },
                {
                    props: { variant: "blue" },
                    style: ({ theme }) => ({
                        background: theme.palette.tagBackgroundBlue.main,
                        "& .MuiChip-label": {
                            color: theme.palette.tagBlue.main,
                        },
                    }),
                },
                {
                    props: { variant: "teal" },
                    style: ({ theme }) => ({
                        background: theme.palette.tagBackgroundTeal.main,
                        "& .MuiChip-label": {
                            color: theme.palette.tagTeal.main,
                        },
                    }),
                },
            ],
            styleOverrides: {
                root: ({ theme }) => ({
                    height: "24px",
                    "& .MuiChip-label": {
                        padding: "0 8px",
                        ...theme.typography.overline,
                    },
                }),
            },
        },
        MuiStep: {
            styleOverrides: {
                root: {
                    maxWidth: "100%",
                    cursor: "pointer",
                    "& *": {
                        cursor: "pointer",
                    },
                },
            },
        },
        MuiStepLabel: {
            styleOverrides: {
                label: ({ theme }) => ({
                    ...theme.typography.overline,
                    [theme.breakpoints.only("xs")]: {
                        display: "none",
                    },
                }),
            },
        },
        MuiStepIcon: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.extendedGrey1.main,
                    "&.Mui-completed": {
                        color: theme.palette.primaryPurple.main, // Completed state color
                    },
                }),
                text: ({ theme }) => ({
                    ...theme.typography.overline,
                }),
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    marginTop: "0.5rem",
                },
                list: {
                    paddingTop: 0,
                    paddingBottom: 0,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: () => ({
                    padding: "1rem",
                }),
            },
        },
    },
    breakpoints: {
        values: BREAKPOINT_VALUES,
    },
};

const lightThemePalette = {
    background: {
        default: lightColors.primaryWhite,
        paper: lightColors.primaryWhite,
    },
    primaryBlack: {
        main: lightColors.primaryBlack,
        contrastText: lightColors.primaryWhite,
    },
    primaryGreen: {
        main: lightColors.primaryGreen,
        contrastText: lightColors.primaryWhite,
    },
    primaryPurple: {
        main: lightColors.primaryPurple,
        dark: lightColors.primaryPurple2,
        darker: lightColors.primaryPurple3,
        contrastText: lightColors.primaryWhite,
    },
    primaryPurple2: {
        main: lightColors.primaryPurple2,
        contrastText: lightColors.primaryWhite,
    },
    primaryPurple3: {
        main: lightColors.primaryPurple3,
        contrastText: lightColors.primaryWhite,
    },
    primaryWhite: {
        main: lightColors.primaryWhite,
        contrastText: lightColors.defaultFontColor,
    },
    primaryBlue: {
        main: lightColors.primaryBlue,
        contrastText: lightColors.defaultFontColor,
    },
    secondaryBlue: {
        main: lightColors.secondaryBlue,
        contrastText: lightColors.defaultFontColor,
    },
    secondaryGreen: {
        main: lightColors.secondaryGreen,
        contrastText: lightColors.primaryWhite,
    },
    secondaryPurple: {
        main: lightColors.secondaryPurple,
        contrastText: lightColors.primaryWhite,
    },
    secondaryRed: {
        main: lightColors.secondaryRed,
        contrastText: lightColors.primaryWhite,
    },
    extendedGreen1: {
        main: lightColors.extendedGreen1,
        contrastText: lightColors.defaultFontColor,
    },
    extendedGreen2: {
        main: lightColors.extendedGreen2,
        contrastText: lightColors.defaultFontColor,
    },
    extendedGreen3: {
        main: lightColors.extendedGreen3,
        contrastText: lightColors.defaultFontColor,
    },
    extendedGreen4: {
        main: lightColors.extendedGreen4,
        contrastText: lightColors.defaultFontColor,
    },
    extendedGreen5: {
        main: lightColors.extendedGreen5,
        contrastText: lightColors.defaultFontColor,
    },
    extendedYellow: {
        main: lightColors.extendedYellow,
        contrastText: lightColors.defaultFontColor,
    },
    extendedGrey1: {
        main: lightColors.extendedGrey1,
        contrastText: lightColors.primaryWhite,
    },
    extendedGrey2: {
        main: lightColors.extendedGrey2,
        contrastText: lightColors.defaultFontColor,
    },
    extendedGrey3: {
        main: lightColors.extendedGrey3,
        contrastText: lightColors.defaultFontColor,
    },
    extendedGrey4: {
        main: lightColors.extendedGrey4,
        contrastText: lightColors.defaultFontColor,
    },
    extendedPurple: {
        main: lightColors.extendedPurple,
        contrastText: lightColors.defaultFontColor,
    },
    extendedPurple2: {
        main: lightColors.extendedPurple2,
        contrastText: lightColors.defaultFontColor,
    },
    highlight: {
        main: lightColors.highlight,
        contrastText: lightColors.defaultFontColor,
    },
    extendedPink1: {
        main: lightColors.extendedPink1,
        contrastText: lightColors.defaultFontColor,
    },
    tagGreen: {
        main: lightColors.tagGreen,
        contrastText: lightColors.defaultFontColor,
    },
    tagBackgroundGreen: {
        main: lightColors.tagBackgroundGreen,
        contrastText: lightColors.defaultFontColor,
    },
    tagRed: {
        main: lightColors.tagRed,
        contrastText: lightColors.defaultFontColor,
    },
    tagBackgroundRed: {
        main: lightColors.tagBackgroundRed,
        contrastText: lightColors.defaultFontColor,
    },
    tagMagenta: {
        main: lightColors.tagMagenta,
        contrastText: lightColors.defaultFontColor,
    },
    tagBackgroundMagenta: {
        main: lightColors.tagBackgroundMagenta,
        contrastText: lightColors.tagMagenta,
    },
    tagBrown: {
        main: lightColors.tagBrown,
        contrastText: lightColors.defaultFontColor,
    },
    tagBackgroundBrown: {
        main: lightColors.tagBackgroundBrown,
        contrastText: lightColors.tagBrown,
    },
    tagGray: {
        main: lightColors.tagGray,
        contrastText: lightColors.defaultFontColor,
    },
    tagBackgroundGray: {
        main: lightColors.tagBackgroundGray,
        contrastText: lightColors.defaultFontColor,
    },
    tagPurple: {
        main: lightColors.tagPurple,
        contrastText: lightColors.defaultFontColor,
    },
    tagBackgroundPurple: {
        main: lightColors.tagBackgroundPurple,
        contrastText: lightColors.defaultFontColor,
    },
    navBackground: {
        main: lightColors.navBackground,
        contrastText: lightColors.defaultFontColor,
    },
    iconPrimary: {
        main: lightColors.iconPrimary,
        contrastText: lightColors.defaultFontColor,
    },
    supportWarning: {
        main: lightColors.supportWarning,
        contrastText: lightColors.defaultFontColor,
    },
    borderDisabled: {
        main: lightColors.borderDisabled,
        contrastText: lightColors.defaultFontColor,
    },
    borderSubtle: {
        main: lightColors.borderSubtle,
        contrastText: lightColors.defaultFontColor,
    },
    tagBlue: {
        main: lightColors.tagBlue,
        contrastText: lightColors.defaultFontColor,
    },
    tagBackgroundBlue: {
        main: lightColors.tagBackgroundBlue,
        contrastText: lightColors.tagBlue,
    },
    tagTeal: {
        main: lightColors.tagTeal,
        contrastText: lightColors.defaultFontColor,
    },
    tagBackgroundTeal: {
        main: lightColors.tagBackgroundTeal,
        contrastText: lightColors.tagTeal,
    },
    marketOverviewBlue: {
        main: lightColors.marketOverviewBlue,
        contrastText: lightColors.primaryWhite,
    },
    notificationErrorBackground: {
        main: lightColors.notificationErrorBackground,
        contrastText: lightColors.defaultFontColor,
    },
};
export const lightTheme = createTheme({
    ...themeOptions,
    palette: lightThemePalette,
    // @ts-ignore
    styles: themeOptions.styles(lightThemePalette) as Styles,
});

const darkThemePalette = {
    mode: "dark",
    background: {
        default: darkColors.primaryWhite,
        paper: darkColors.primaryWhite,
    },
    primaryBlack: {
        main: darkColors.primaryBlack,
        contrastText: darkColors.primaryWhite,
    },
    primaryGreen: {
        main: darkColors.primaryGreen,
        contrastText: darkColors.primaryBlack,
    },
    primaryPurple: {
        main: darkColors.primaryPurple,
        dark: darkColors.primaryPurple,
        darker: darkColors.primaryPurple,
        contrastText: darkColors.primaryWhite,
    },
    primaryPurple2: {
        main: darkColors.primaryPurple2,
        contrastText: darkColors.primaryWhite,
    },
    primaryPurple3: {
        main: darkColors.primaryPurple3,
        contrastText: darkColors.primaryBlack,
    },
    primaryWhite: {
        main: darkColors.primaryWhite,
        contrastText: darkColors.defaultFontColor,
    },
    primaryBlue: {
        main: darkColors.primaryBlue,
        contrastText: darkColors.defaultFontColor,
    },
    secondaryBlue: {
        main: darkColors.secondaryBlue,
        contrastText: darkColors.defaultFontColor,
    },
    secondaryGreen: {
        main: darkColors.secondaryGreen,
        contrastText: darkColors.primaryWhite,
    },
    secondaryPurple: {
        main: darkColors.secondaryPurple,
        contrastText: darkColors.primaryWhite,
    },
    secondaryRed: {
        main: darkColors.secondaryRed,
        contrastText: darkColors.primaryWhite,
    },
    extendedGreen1: {
        main: darkColors.extendedGreen1,
        contrastText: darkColors.defaultFontColor,
    },
    extendedGreen2: {
        main: darkColors.extendedGreen2,
        contrastText: darkColors.defaultFontColor,
        light: lightColors.extendedGreen2,
    },
    extendedGreen3: {
        main: darkColors.extendedGreen3,
        contrastText: darkColors.defaultFontColor,
    },
    extendedGreen4: {
        main: darkColors.extendedGreen4,
        contrastText: darkColors.defaultFontColor,
    },
    extendedGreen5: {
        main: darkColors.extendedGreen5,
        contrastText: darkColors.defaultFontColor,
    },
    extendedYellow: {
        main: darkColors.extendedYellow,
        contrastText: darkColors.primaryWhite,
    },
    extendedGrey1: {
        main: darkColors.extendedGrey1,
        contrastText: darkColors.primaryWhite,
    },
    extendedGrey2: {
        main: darkColors.extendedGrey2,
        contrastText: darkColors.defaultFontColor,
    },
    extendedGrey3: {
        main: darkColors.extendedGrey3,
        contrastText: darkColors.defaultFontColor,
    },
    extendedGrey4: {
        main: darkColors.extendedGrey4,
        contrastText: darkColors.defaultFontColor,
    },
    extendedPurple: {
        main: darkColors.extendedPurple,
        contrastText: darkColors.defaultFontColor,
    },
    extendedPurple2: {
        main: darkColors.extendedPurple2,
        contrastText: darkColors.defaultFontColor,
    },
    highlight: {
        main: darkColors.highlight,
        contrastText: darkColors.primaryWhite,
    },
    extendedPink1: {
        main: darkColors.extendedPink1,
        contrastText: darkColors.defaultFontColor,
    },
    tagGreen: {
        main: darkColors.tagGreen,
        contrastText: darkColors.defaultFontColor,
    },
    tagBackgroundGreen: {
        main: darkColors.tagBackgroundGreen,
        contrastText: darkColors.defaultFontColor,
    },
    tagRed: {
        main: darkColors.tagRed,
        contrastText: darkColors.defaultFontColor,
    },
    tagBackgroundRed: {
        main: darkColors.tagBackgroundRed,
        contrastText: darkColors.defaultFontColor,
    },
    tagGray: {
        main: darkColors.tagGray,
        contrastText: darkColors.defaultFontColor,
    },
    tagBackgroundGray: {
        main: darkColors.tagBackgroundGray,
        contrastText: darkColors.defaultFontColor,
    },
    tagPurple: {
        main: darkColors.tagPurple,
        contrastText: darkColors.primaryWhite,
    },
    tagBackgroundPurple: {
        main: darkColors.tagBackgroundPurple,
        contrastText: darkColors.defaultFontColor,
    },
    tagMagenta: {
        main: lightColors.tagMagenta,
        contrastText: lightColors.defaultFontColor,
    },
    tagBackgroundMagenta: {
        main: lightColors.tagBackgroundMagenta,
        contrastText: lightColors.tagMagenta,
    },
    tagBrown: {
        main: lightColors.tagBrown,
        contrastText: lightColors.defaultFontColor,
    },
    tagBackgroundBrown: {
        main: lightColors.tagBackgroundBrown,
        contrastText: lightColors.tagBrown,
    },
    navBackground: {
        main: darkColors.navBackground,
        contrastText: lightColors.defaultFontColor,
    },
    iconPrimary: {
        main: darkColors.iconPrimary,
        contrastText: lightColors.defaultFontColor,
    },
    supportWarning: {
        main: lightColors.supportWarning,
        contrastText: lightColors.defaultFontColor,
    },
    borderDisabled: {
        main: darkColors.borderDisabled,
        contrastText: lightColors.defaultFontColor,
    },
    borderSubtle: {
        main: lightColors.extendedGreen2,
        contrastText: lightColors.defaultFontColor,
    },
    tagBlue: {
        main: lightColors.tagBlue,
        contrastText: lightColors.defaultFontColor,
    },
    tagBackgroundBlue: {
        main: lightColors.tagBackgroundBlue,
        contrastText: lightColors.tagBlue,
    },
    tagTeal: {
        main: lightColors.tagTeal,
        contrastText: lightColors.defaultFontColor,
    },
    tagBackgroundTeal: {
        main: lightColors.tagBackgroundTeal,
        contrastText: lightColors.tagTeal,
    },
    marketOverviewBlue: {
        main: darkColors.marketOverviewBlue,
        contrastText: darkColors.primaryWhite,
    },
    notificationErrorBackground: {
        main: darkColors.notificationErrorBackground,
        contrastText: lightColors.defaultFontColor,
    },
};
export const darkTheme = createTheme({
    ...themeOptions,
    // @ts-ignore
    palette: darkThemePalette,
    // @ts-ignore
    styles: themeOptions.styles(darkThemePalette) as Styles,
});

const redGreenColorblindThemePalette = {
    background: {
        default: redGreenColorblindColors.primaryWhite,
        paper: redGreenColorblindColors.primaryWhite,
    },
    primaryBlack: {
        main: redGreenColorblindColors.primaryBlack,
        contrastText: redGreenColorblindColors.primaryWhite,
    },
    primaryGreen: {
        main: redGreenColorblindColors.primaryGreen,
        contrastText: redGreenColorblindColors.primaryWhite,
    },
    primaryPurple: {
        main: redGreenColorblindColors.primaryPurple,
        dark: redGreenColorblindColors.primaryPurple2,
        darker: redGreenColorblindColors.primaryPurple3,
        contrastText: redGreenColorblindColors.primaryWhite,
    },
    primaryPurple2: {
        main: redGreenColorblindColors.primaryPurple2,
        contrastText: redGreenColorblindColors.primaryWhite,
    },
    primaryPurple3: {
        main: redGreenColorblindColors.primaryPurple3,
        contrastText: redGreenColorblindColors.primaryWhite,
    },
    primaryWhite: {
        main: redGreenColorblindColors.primaryWhite,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    primaryBlue: {
        main: redGreenColorblindColors.primaryBlue,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    secondaryBlue: {
        main: redGreenColorblindColors.secondaryBlue,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    secondaryGreen: {
        main: redGreenColorblindColors.secondaryGreen,
        contrastText: redGreenColorblindColors.primaryWhite,
    },
    secondaryPurple: {
        main: redGreenColorblindColors.secondaryPurple,
        contrastText: redGreenColorblindColors.primaryWhite,
    },
    secondaryRed: {
        main: redGreenColorblindColors.secondaryRed,
        contrastText: redGreenColorblindColors.primaryWhite,
    },
    extendedGreen1: {
        main: redGreenColorblindColors.extendedGreen1,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    extendedGreen2: {
        main: redGreenColorblindColors.extendedGreen2,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    extendedGreen3: {
        main: redGreenColorblindColors.extendedGreen3,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    extendedGreen4: {
        main: redGreenColorblindColors.extendedGreen4,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    extendedGreen5: {
        main: redGreenColorblindColors.extendedGreen5,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    extendedYellow: {
        main: redGreenColorblindColors.extendedYellow,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    extendedGrey1: {
        main: redGreenColorblindColors.extendedGrey1,
        contrastText: redGreenColorblindColors.primaryWhite,
    },
    extendedGrey2: {
        main: redGreenColorblindColors.extendedGrey2,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    extendedGrey3: {
        main: redGreenColorblindColors.extendedGrey3,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    extendedGrey4: {
        main: redGreenColorblindColors.extendedGrey4,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    extendedPurple: {
        main: redGreenColorblindColors.extendedPurple,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    extendedPurple2: {
        main: redGreenColorblindColors.extendedPurple2,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    highlight: {
        main: redGreenColorblindColors.highlight,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    extendedPink1: {
        main: redGreenColorblindColors.extendedPink1,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    tagGreen: {
        main: redGreenColorblindColors.tagGreen,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    tagBackgroundGreen: {
        main: redGreenColorblindColors.tagBackgroundGreen,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    tagRed: {
        main: redGreenColorblindColors.tagRed,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    tagBackgroundRed: {
        main: redGreenColorblindColors.tagBackgroundRed,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    tagGray: {
        main: redGreenColorblindColors.tagGray,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    tagBackgroundGray: {
        main: redGreenColorblindColors.tagBackgroundGray,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    tagPurple: {
        main: redGreenColorblindColors.tagPurple,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    tagBackgroundPurple: {
        main: redGreenColorblindColors.tagBackgroundPurple,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    tagMagenta: {
        main: lightColors.tagMagenta,
        contrastText: lightColors.defaultFontColor,
    },
    tagBackgroundMagenta: {
        main: lightColors.tagBackgroundMagenta,
        contrastText: lightColors.tagMagenta,
    },
    tagBrown: {
        main: lightColors.tagBrown,
        contrastText: lightColors.defaultFontColor,
    },
    tagBackgroundBrown: {
        main: lightColors.tagBackgroundBrown,
        contrastText: lightColors.tagBrown,
    },
    navBackground: {
        main: lightColors.navBackground,
        contrastText: lightColors.defaultFontColor,
    },
    iconPrimary: {
        main: lightColors.iconPrimary,
        contrastText: lightColors.defaultFontColor,
    },
    supportWarning: {
        main: lightColors.supportWarning,
        contrastText: lightColors.defaultFontColor,
    },
    borderDisabled: {
        main: lightColors.borderDisabled,
        contrastText: redGreenColorblindColors.defaultFontColor,
    },
    borderSubtle: {
        main: lightColors.extendedGreen2,
        contrastText: lightColors.defaultFontColor,
    },

    tagBlue: {
        main: lightColors.tagBlue,
        contrastText: lightColors.defaultFontColor,
    },
    tagBackgroundBlue: {
        main: lightColors.tagBackgroundBlue,
        contrastText: lightColors.tagBlue,
    },
    tagTeal: {
        main: lightColors.tagTeal,
        contrastText: lightColors.defaultFontColor,
    },
    tagBackgroundTeal: {
        main: lightColors.tagBackgroundTeal,
        contrastText: lightColors.tagTeal,
    },
    marketOverviewBlue: {
        main: redGreenColorblindColors.marketOverviewBlue,
        contrastText: redGreenColorblindColors.primaryWhite,
    },
    notificationErrorBackground: {
        main: lightColors.notificationErrorBackground,
        contrastText: lightColors.defaultFontColor,
    },
};
export const redGreenColorblindTheme = createTheme({
    ...themeOptions,
    palette: redGreenColorblindThemePalette,
    // @ts-ignore
    styles: themeOptions.styles(redGreenColorblindThemePalette) as Styles,
});

export type TThemeMode = "light" | "dark" | "red-green-colorblind";

const themePalettes = {
    light: lightThemePalette,
    dark: darkThemePalette,
    "red-green-colorblind": redGreenColorblindThemePalette,
};

const themeCache = new Map<string, Theme>();
const viewportThemes = new WeakMap<Theme, Theme>();

/**
 * The sibling of `theme` whose breakpoints measure the whole window instead of
 * the nav-inset content area — i.e. the same colours and components built at an
 * inset of 0. Returns `theme` untouched when it already measures the window, or
 * when it wasn't built by `createHwyTheme`.
 */
export const getViewportTheme = (theme: Theme): Theme => viewportThemes.get(theme) ?? theme;

/**
 * Builds the theme for a colour mode and a given nav-rail inset.
 *
 * `sidebarInset` is the horizontal space the nav rail steals from the page (0
 * when there's no rail). Breakpoints are offset by it, so `theme.breakpoints.up`,
 * `sx` breakpoint objects and `useScreenSize` all resolve against usable content
 * width and re-resolve when the user collapses or expands the rail.
 *
 * Themes are cached per mode/inset pair — `createTheme` over these options isn't
 * cheap, and there are only a handful of valid combinations.
 *
 * That cache is unbounded, which is only safe because `sidebarInset` comes from a
 * fixed set: 0, SIDEBAR_COLLAPSED_WIDTH, or SIDEBAR_WIDTH, for at most nine
 * entries. Anything that made the inset continuous — a drag-resizable rail, say —
 * would need to bound or evict from it.
 */
export const createHwyTheme = (mode: TThemeMode = "light", sidebarInset = 0): Theme => {
    const cacheKey = `${mode}:${sidebarInset}`;
    const cached = themeCache.get(cacheKey);
    if (cached) return cached;

    const palette = themePalettes[mode];
    const theme = createTheme({
        ...themeOptions,
        // @ts-ignore
        palette,
        // @ts-ignore
        styles: themeOptions.styles(palette) as Styles,
        breakpoints: {
            values: offsetBreakpointValues(sidebarInset),
        },
    });

    themeCache.set(cacheKey, theme);
    // Link the theme to its inset-0 sibling so anything spanning the full window
    // can switch to it without having to know which mode it's in.
    viewportThemes.set(theme, sidebarInset === 0 ? theme : createHwyTheme(mode, 0));
    return theme;
};

// Seed the cache with the standalone exports so an inset of 0 hands back the
// very same object those consumers already hold, instead of a duplicate.
themeCache.set("light:0", lightTheme);
themeCache.set("dark:0", darkTheme);
themeCache.set("red-green-colorblind:0", redGreenColorblindTheme);
[lightTheme, darkTheme, redGreenColorblindTheme].forEach((theme) => viewportThemes.set(theme, theme));

export const HTheme = lightTheme; // REVIEW possibly delete this
